import {ReportTaxonomyField} from '../contracts/report-taxonomy.types';
import {englishLabel, FormatLabel, ragLabelFor} from '../projectStatusLabels';
import {BUDGET_TYPE_ALIASES, BUDGET_TYPE_FILTER_ORDER, budgetTypeFilterLabelFor} from '../projectBudgetTypeLabels';
import {stageLabelFor} from '../projectStageLabels';
import {winChanceLabelFor} from '../projectWinChanceLabels';
import {humanizeToken} from '../tokenLabel';
import {ValueCandidate} from './valueSources/valueSource.types';

/** The audit log's vocabularies are raw wire tokens throughout (`STATUS_CHANGE`, `FORCE_APPROVE`),
 * and its table humanises every one of them - see `ReportTable/cellDisplay`. The picker has to use
 * the same rule or a reader filters on `STATUS_CHANGE` beside a column reading `Status change`.
 * Shared, not restated: both surfaces call `humanizeToken`. */
const humanizedToken = (value: string): string => humanizeToken(value);

/** Filter fields whose picker offers one value at a time, even though the catalog declares the
 * multi-value operators (IN/NOT_IN) for them.
 *
 * This is a deliberate narrowing of the INTERFACE, not a limit of the contract. Progress ships as
 * single-select because that is the decision the ticket records; widening it to multi-select later
 * needs nothing from report-service, only this set. Keeping it a set of filter fields rather than a
 * flag inferred from the unit or data type means the narrowing is stated where it can be read and
 * removed, instead of being a side effect of some other property.
 *
 * Win chance is narrowed for a stronger reason than progress: its bands OVERLAP. A project at 60%
 * satisfies both `over 30 %` and `over 50 %`, so a multi-select offering both reads as a union the
 * reader did not intend and cannot see the effect of - picking `over 30 %` and `over 50 %` together
 * returns exactly what `over 30 %` alone returns. One band at a time is the only selection whose
 * result a reader can predict. The contract still declares IN/NOT_IN, so widening this later needs
 * nothing from report-service.
 *
 * Retainer over/under is deliberately absent: nothing asked for it to be narrowed, and the catalog
 * declares IN/NOT_IN, so it stays multi-select. */
const SINGLE_SELECT_FILTER_FIELDS = new Set(['PROGRESS_BUCKET', 'WIN_CHANCE_BAND']);

export function isSingleSelectEnumeration(field: ReportTaxonomyField): boolean {
	return field.filterField !== null && SINGLE_SELECT_FILTER_FIELDS.has(field.filterField);
}

/** Filter fields whose picker values are shown through a display vocabulary rather than as the raw
 * wire token, and the vocabulary each is shown through.
 *
 * Keyed by filter field rather than by catalog key so the picker and the column are matched on the
 * same identifier a condition carries. Kept to the fields that actually have a display vocabulary:
 * every other enumerated field publishes values a reader can already read, and turning this into a
 * general relabelling hook would invent prose for vocabularies report-service owns.
 *
 * For PROJECT_STATUS_RAG and PROJECT_STAGE the vocabulary is the TABLE's: the cell and the group
 * heading render the same strings, so the picker beside the column cannot read differently.
 * WIN_CHANCE_BAND is here for a different reason - its column is a whole percentage (`45%`), not a
 * band, so there is no column wording to agree with; see the entry below.
 *
 * A `Map` rather than an object literal because the key is a raw catalog string. */
const DISPLAY_VOCABULARY = new Map<string, (value: string, formatLabel: FormatLabel) => string>([
	['PROJECT_STATUS_RAG', ragLabelFor],
	['PROJECT_STAGE', stageLabelFor],
	// Win chance. Without an entry here its tokens fall through to the percent-unit branch below
	// and render as `ZERO%` and `OVER_10%` - the unit suffix applied to a band token rather than to
	// a number. The vocabulary also states the overlap the tokens hide: `over 10 %`, not `10 %`.
	['WIN_CHANCE_BAND', winChanceLabelFor],
	// Budget type. Its picker words are the standard report's (plural Retainers, Non-Billable), not
	// the column's singular ones - DECIDED on TNSR-638, so a reader picking a category meets the same
	// list on both report surfaces.
	['PROJECT_BUDGET_TYPE', budgetTypeFilterLabelFor],
	// Every enumerated audit field. Listed one by one rather than matched on an `AUDIT_` prefix: the
	// entry is what says a vocabulary has a display form, and a prefix rule would silently claim one
	// for an audit field added later whose values are already readable.
	['AUDIT_EVENT_TYPE', humanizedToken],
	['AUDIT_FROM_STATUS', humanizedToken],
	['AUDIT_TO_STATUS', humanizedToken],
	['AUDIT_TIME_TYPE', humanizedToken],
	['AUDIT_APPROVAL_TYPE', humanizedToken],
	['AUDIT_ACTOR_SOURCE', humanizedToken],
	['AUDIT_FIELD_NAME', humanizedToken],
	['AUDIT_IS_MATERIAL', humanizedToken],
]);

/** The label a stored value is shown as.
 *
 * A field the table relabels is relabelled here too, with the same vocabulary the cell and the
 * group heading use. Without that the picker beside the column offers `Green` and `HALTED` while
 * the column reads `On Track` and `Halted` - one field reading two ways on two surfaces, which is
 * the thing the enumerated kind exists to prevent.
 *
 * Otherwise the value itself is the label. A percent-unit field appends the symbol, because the
 * catalog publishes the bands as bare ranges (`0-25`) while a reader has to be told what the range
 * counts - `0-25` on its own reads as a count of something, not a proportion. Nothing else is
 * invented: the band boundaries and their spelling come from the catalog, so the four bands a
 * reader is offered are by construction the four the engine can apply, and a catalog that changes
 * them changes the picker with no code change here. */
function labelFor(value: string, field: ReportTaxonomyField, formatLabel: FormatLabel): string {
	const display = field.filterField === null ? undefined : DISPLAY_VOCABULARY.get(field.filterField);
	if (display) {
		// An empty answer means the vocabulary has no label for this value, not that the label is
		// blank; echo the stored value, which is what the rest of this module does with one.
		return display(value, formatLabel) || value;
	}
	return field.unit === 'percent' ? `${value}%` : value;
}

/** Stored values a field's picker shows as another value's option, keyed by filter field.
 *
 * Budget type's two fixed-price enums are one option to a reader. That is safe because report-service
 * widens a filter on either to both, so which one a condition holds changes nothing about its result -
 * only whether the picker would show it as a second, identically-labelled row. */
const VALUE_ALIASES = new Map<string, ReadonlyMap<string, string>>([['PROJECT_BUDGET_TYPE', BUDGET_TYPE_ALIASES]]);

/** Option order where a field's has been decided rather than taken from the catalog. A value the list
 * does not name keeps its catalog order after the named ones, so a value the catalog adds later is
 * still offered rather than silently dropped. */
const CANDIDATE_ORDER = new Map<string, readonly string[]>([['PROJECT_BUDGET_TYPE', BUDGET_TYPE_FILTER_ORDER]]);

function aliasesFor(field: ReportTaxonomyField | undefined): ReadonlyMap<string, string> | undefined {
	return field?.filterField ? VALUE_ALIASES.get(field.filterField) : undefined;
}

/** A condition's stored values as its picker's option ids: an alias replaced by the value it folds
 * onto, duplicates dropped, order kept. The stored array itself for a field with no aliases.
 *
 * Applied wherever a condition's values are READ for display - the picker's ticks and the summary -
 * so a condition the agent drafted with the legacy `FIXED_PRICE` shows as the one ticked Fixed Price,
 * and editing it writes the canonical id. */
export function canonicalEnumeratedValues(field: ReportTaxonomyField | undefined, values: string[]): string[] {
	const aliases = aliasesFor(field);
	if (!aliases) {
		return values;
	}
	const canonical: string[] = [];
	for (const value of values) {
		const id = aliases.get(value) ?? value;
		if (!canonical.includes(id)) {
			canonical.push(id);
		}
	}
	return canonical;
}

function offeredValues(field: ReportTaxonomyField): string[] {
	const aliases = aliasesFor(field);
	const values = (field.values ?? []).filter(value => !aliases?.has(value));
	const order = field.filterField ? CANDIDATE_ORDER.get(field.filterField) : undefined;
	if (!order) {
		return values;
	}
	return [...order.filter(value => values.includes(value)), ...values.filter(value => !order.includes(value))];
}

/** Picker candidates built from the vocabulary the catalog publishes for this field. The candidate
 * id is the stored value verbatim, so what the picker writes into the condition is exactly what
 * report-service's resolver-side filter compares against. */
export function enumeratedCandidates(field: ReportTaxonomyField, formatLabel: FormatLabel = englishLabel): ValueCandidate[] {
	return offeredValues(field).map(value => ({id: value, label: labelFor(value, field, formatLabel)}));
}

/** The same labels as a resolver a summary can call without holding a `ValueSource`.
 *
 * `formatConditionSummary` is rendered on two surfaces: the builder tile, which passes the tile's
 * own value source, and the chat intent card, which has none. Without this the card would show the
 * bare stored band (`51-75`) while the tile beside it shows `51-75%` - the very "two spellings of
 * one condition" the enumerated kind exists to remove, just moved to another surface. An undeclared
 * value echoes itself, exactly as the picker's source does. */
export function enumeratedLabelResolver(
	field: ReportTaxonomyField,
	formatLabel: FormatLabel = englishLabel
): (value: string) => string {
	const byId = new Map(enumeratedCandidates(field, formatLabel).map(candidate => [candidate.id, candidate.label]));
	// An alias is not itself offered, so it is named through the option it folds onto.
	const aliases = aliasesFor(field);
	return value => byId.get(aliases?.get(value) ?? value) ?? value;
}
